import { Button } from "./Button";
import { Reveal } from "./wow/Reveal";
import { Magnetic } from "./wow/MagneticButton";
import { useLocale } from "@/i18n/LocaleProvider";

interface TrustBandProps {
  eyebrow?: string;
  title?: string;
  body?: string;
  ctaLocation?: string;
  showBrands?: boolean;
}

export function TrustBand(props: TrustBandProps) {
  const locale = useLocale();
  const en = locale === "en";

  const eyebrow = props.eyebrow ?? "Norrhavet Group";
  const title =
    props.title ??
    (en ? "Three brands. One seamless process." : "Tre varumärken. En sömlös process.");
  const body =
    props.body ??
    (en
      ? "Hasty delivers the marketing department on subscription, Fenny produces the video, Ellary scales the marketing. You get one project lead, one invoice, one creative direction."
      : "Hasty levererar marknadsavdelningen på abonnemang, Fenny producerar filmen, Ellary skalar marknadsföringen. Ni får en projektledare, en faktura, en kreativ riktning.");
  const ctaLocation = props.ctaLocation ?? "trust_band";
  const showBrands = props.showBrands ?? true;

  const aboutLabel = en ? "About Fenny" : "Om Fenny";
  const bookLabel = en ? "Book a strategy call" : "Boka strategimöte";

  const brandsSv = [
    { tag: "Hasty", body: "Marknadsavdelning på abonnemang — kreativ kapacitet med leverans inom 48 timmar.", href: "https://hasty.se", linkLabel: "Besök hasty.se" },
    { tag: "Fenny", body: "Premiumfilm för företag — abonnemang eller fast pris, alltid hög kvalitet.", href: "https://fenny.se", linkLabel: "Besök fenny.se" },
    { tag: "Ellary", body: "Marknadsföring till fasta priser — SEO, sociala medier och annonsering.", href: "https://ellary.se", linkLabel: "Besök ellary.se" },
  ];
  const brandsEn = [
    { tag: "Hasty", body: "Marketing department on subscription — creative capacity with delivery within 48 hours.", href: "https://hasty.se", linkLabel: "Visit hasty.se" },
    { tag: "Fenny", body: "Premium video for companies — subscription or fixed price, always high quality.", href: "https://fenny.se", linkLabel: "Visit fenny.se" },
    { tag: "Ellary", body: "Marketing at fixed prices — SEO, social media and paid ads.", href: "https://ellary.se", linkLabel: "Visit ellary.se" },
  ];
  const brands = en ? brandsEn : brandsSv;


  return (
    <section className="border-y border-white/10 bg-surface/40">
      <div className="container-wide py-16 md:py-20">
        <div className="grid items-end gap-10 md:grid-cols-[1.5fr_1fr]">
          <div>
            <div className="mb-3 text-xs uppercase tracking-[0.18em] text-mint">{eyebrow}</div>
            <h2 className="text-balance text-3xl font-semibold leading-tight md:text-5xl">{title}</h2>
            <p className="mt-5 max-w-2xl text-pretty text-base text-muted-foreground md:text-lg">{body}</p>
          </div>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Button
                to={en ? "/en/about" : "/om-fenny"}
                variant="outline"
                trackId="how_pulse_works"
                trackProps={{ location: ctaLocation }}
              >
                {aboutLabel}
              </Button>
              <Magnetic>
                <Button
                  to={en ? "/en/contact" : "/kontakt"}
                  variant="mint"
                  arrow
                  trackId="book_strategy_meeting"
                  trackProps={{ location: ctaLocation }}
                >
                  {bookLabel}
                </Button>
              </Magnetic>
            </div>
          </Reveal>
        </div>

        {showBrands && (
          <ul className="mt-10 grid gap-4 md:grid-cols-3">
            {brands.map((b) => (
              <li key={b.tag} className="rounded-2xl border border-white/10 bg-background p-6">
                <span className="text-lg font-semibold text-mint">{b.tag}</span>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.body}</p>
                <a
                  href={b.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-foreground underline-offset-4 hover:text-mint hover:underline"
                >
                  {b.linkLabel} <span aria-hidden>↗</span>
                </a>
              </li>
            ))}
          </ul>
        )}

      </div>
    </section>
  );
}
